/**
 * Quest Creation Management Component
 * Shows details on every quest created by each user with analytics
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  IconButton,
  Skeleton,
  Tooltip,
} from '@mui/material';
import {
  EmojiEvents as QuestIcon,
  Assessment as AnalyticsIcon,
  Visibility as ViewIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import { useDispatch } from 'react-redux';
import { AppDispatch } from '../../store';
import { addNotification } from '../../store/uiSlice';
import { format, formatDistanceToNow } from 'date-fns';
import apiService from '../../services/api';
import StatusBadge from '../common/StatusBadge';
import { colors, spacing } from '../../theme/designTokens';
import QuestViewModal from './QuestViewModal';
import QuestAnalyticsTab from './QuestAnalyticsTab';

interface Quest {
  id: string;
  title: string;
  story: string;
  originalText: string;
  status: 'draft' | 'completed';
  created: string;
  updated: string;
  wordCount: number;
  writingStyle?: string;
  illustrationStyle?: string;
  mood?: string;
  user: {
    id: string;
    email: string;
    subscriptionPlan?: string;
  };
  questPromptId?: number;
  source?: 'quest' | 'legacy_quest';
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`quest-tabpanel-${index}`}
      aria-labelledby={`quest-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
    </div>
  );
}

export const QuestCreation: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();

  const [tabValue, setTabValue] = useState(0);
  const [quests, setQuests] = useState<Quest[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalQuests, setTotalQuests] = useState(0);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedQuest, setSelectedQuest] = useState<any>(null);

  const loadQuests = async () => {
    try {
      setLoading(true);
      const response = await apiService.getQuests({
        page: page,
        limit: rowsPerPage,
      });

      if (response && response.quests) {
        setQuests(response.quests);
        setTotalQuests(response.totalCount || 0);
      } else {
        setQuests([]);
        setTotalQuests(0);
      }

      dispatch(addNotification({
        type: 'success',
        message: 'Quest data loaded successfully',
      }));
    } catch (error) {
      console.error('Failed to load quests:', error);
      setQuests([]);
      setTotalQuests(0);

      dispatch(addNotification({
        type: 'error',
        message: 'Failed to load quest data - check backend connection',
      }));
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    await loadQuests();
    dispatch(addNotification({
      type: 'success',
      message: 'Quest data refreshed',
    }));
  };

  const handleViewQuest = async (questId: string) => {
    try {
      const questData = await apiService.getQuestById(questId);
      setSelectedQuest(questData);
      setViewDialogOpen(true);
    } catch (error) {
      console.error('View quest error:', error);
      dispatch(addNotification({
        type: 'error',
        message: 'Failed to load quest details',
      }));
    }
  };

  useEffect(() => {
    loadQuests();
  }, [page, rowsPerPage]);

  return (
    <Box sx={{ p: spacing[6], bgcolor: 'background.default', minHeight: '100vh' }}>
      {/* Header */}
      <Box sx={{ mb: spacing[8] }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography
            variant="h1"
            sx={{
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              gap: spacing[3],
              mb: spacing[2]
            }}
          >
            <QuestIcon sx={{ fontSize: 40, color: colors.content.main }} />
            Quest Creation
          </Typography>
          <IconButton onClick={handleRefresh}>
            <RefreshIcon />
          </IconButton>
        </Box>
        <Typography variant="body1" color="text.secondary" sx={{ fontSize: '1.125rem' }}>
          Monitor and manage all quests created by users from Legacy Quest prompts
        </Typography>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 2, borderColor: colors.content.main, mb: spacing[6] }}>
        <Tabs
          value={tabValue}
          onChange={(_, newValue) => setTabValue(newValue)}
          aria-label="quest management tabs"
          TabIndicatorProps={{
            style: {
              backgroundColor: colors.content.main,
              height: 3,
            }
          }}
        >
          <Tab
            label="Quests Created"
            icon={<QuestIcon />}
            iconPosition="start"
            sx={{
              minHeight: 64,
              fontSize: '1rem',
              fontWeight: 600,
              '&.Mui-selected': {
                color: colors.content.main,
              }
            }}
          />
          <Tab
            label="Quest Analytics"
            icon={<AnalyticsIcon />}
            iconPosition="start"
            sx={{
              minHeight: 64,
              fontSize: '1rem',
              fontWeight: 600,
              '&.Mui-selected': {
                color: colors.content.main,
              }
            }}
          />
        </Tabs>

        {/* Quests Created Tab */}
        <TabPanel value={tabValue} index={0}>
          <Card>
            <CardContent>
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Quest Details</TableCell>
                      <TableCell>Author</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Word Count</TableCell>
                      <TableCell>Writing Style</TableCell>
                      <TableCell>Illustration Style</TableCell>
                      <TableCell>Mood</TableCell>
                      <TableCell>Source</TableCell>
                      <TableCell>Created</TableCell>
                      <TableCell align="right">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {loading ? (
                      Array.from({ length: rowsPerPage }).map((_, index) => (
                        <TableRow key={index}>
                          {Array.from({ length: 10 }).map((_, cellIndex) => (
                            <TableCell key={cellIndex}>
                              <Skeleton variant="text" />
                            </TableCell>
                          ))}
                        </TableRow>
                      ))
                    ) : quests.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={10} align="center">
                          <Typography variant="body2" color="text.secondary" sx={{ py: 4 }}>
                            No quests found. Quests appear here from the Quest Creation flow and from Legacy Quest submissions.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ) : (
                      quests.map((quest) => (
                        <TableRow key={quest.id} hover>
                          <TableCell>
                            <Box>
                              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                {quest.title || 'Untitled Quest'}
                              </Typography>
                              <Typography variant="body2" color="text.secondary" sx={{
                                display: '-webkit-box',
                                WebkitLineClamp: 2,
                                WebkitBoxOrient: 'vertical',
                                overflow: 'hidden',
                                maxWidth: 300
                              }}>
                                {quest.story?.substring(0, 100) || quest.originalText?.substring(0, 100)}...
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Avatar sx={{ width: 32, height: 32 }}>
                                <PersonIcon />
                              </Avatar>
                              <Box>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {quest.user?.email?.split('@')[0] || 'Unknown'}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {quest.user?.email || 'No email'}
                                </Typography>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <StatusBadge
                              status={quest.status === 'completed' ? 'success' : 'warning'}
                              label={quest.status}
                              variant="filled"
                              size="sm"
                            />
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {quest.wordCount?.toLocaleString() || 0} words
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {quest.writingStyle || '\u2014'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {quest.illustrationStyle || '\u2014'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" sx={{ textTransform: 'capitalize' }}>
                              {quest.mood || '\u2014'}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={quest.source === 'legacy_quest' ? 'Legacy Quest' : 'Quest'}
                              color={quest.source === 'legacy_quest' ? 'secondary' : 'default'}
                              size="small"
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>
                            <Tooltip title={format(new Date(quest.created), 'PPP')}>
                              <Typography variant="body2">
                                {formatDistanceToNow(new Date(quest.created), { addSuffix: true })}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell align="right">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewQuest(quest.id)}
                            >
                              <ViewIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              <TablePagination
                component="div"
                count={totalQuests}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </CardContent>
          </Card>
        </TabPanel>

        {/* Quest Analytics Tab */}
        <TabPanel value={tabValue} index={1}>
          <QuestAnalyticsTab />
        </TabPanel>
      </Box>

      {/* View Quest Modal */}
      <QuestViewModal
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        quest={selectedQuest}
      />
    </Box>
  );
};

export default QuestCreation;
